import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchPerDiemClaims, fetchExpenseClaims, deleteExpenseClaim, deletePerDiemClaim } from '../service/dashboardService.mjs';
import { claimInGroup } from '../../utils/statusConfig.mjs';
// , fetchTravelClaims

const useClaimsDashboard = () => {
  // State for claims data
  const [perDiemClaims, setPerDiemClaims] = useState([]);
  const [expenseClaims, setExpenseClaims] = useState([]);
//   const [travelClaims, setTravelClaims] = useState([]);
  
  // State for loading and errors
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [claimType, setClaimType] = useState('perdiem'); // 'perdiem', 'expense', 'travel'
  const [statusFilter, setStatusFilter] = useState(''); // a CLAIMANT_STATUS_GROUPS value, '' = all

  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // All claims of the selected type (unfiltered by status) - used to build the
  // dynamic status filter options.
  const typeClaims = useMemo(() => {
    switch (claimType) {
      case 'perdiem': return perDiemClaims;
      case 'expense': return expenseClaims;
      default: return [];
    }
  }, [claimType, perDiemClaims, expenseClaims]);

  // Active claims = type list filtered by status (generic: supports any status).
  const activeClaims = useMemo(() => {
    if (!statusFilter) return typeClaims;
    // statusFilter holds a claimant GROUP value now, not a raw status - several
    // raw states fold into one option (see CLAIMANT_STATUS_GROUPS).
    return typeClaims.filter((claim) => claimInGroup(claim.status, statusFilter));
  }, [typeClaims, statusFilter]);

  // Clear selection when claimType or statusFilter changes
  const handleSetClaimType = useCallback((type) => {
    setClaimType(type);
    setSelectedIds(new Set());
  }, []);

  const handleSetStatusFilter = useCallback((status) => {
    setStatusFilter(status);
    setSelectedIds(new Set());
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((claims) => {
    setSelectedIds(new Set(claims.map(c => c._id)));
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const deleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    const deleteFn = claimType === 'expense' ? deleteExpenseClaim : deletePerDiemClaim;
    const results = await Promise.allSettled([...selectedIds].map(id => deleteFn(id)));
    const failed = results.filter(r => r.status === 'rejected').length;
    setSelectedIds(new Set());
    setDeleting(false);
    // Refetch from the server so the list reflects what was actually deleted in the DB.
    await fetchAllClaims();
    // Surface any failures so a silent partial delete doesn't look like success.
    if (failed > 0) {
      setError(`${failed} of ${results.length} draft${results.length !== 1 ? 's' : ''} could not be deleted (only draft claims can be deleted). The rest were removed.`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, claimType]);

  // Fetch all claims data
  const fetchAllClaims = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [perDiemData, expenseData] = await Promise.all([
        // , travelData
        fetchPerDiemClaims(),
        fetchExpenseClaims(),
        // fetchTravelClaims()
      ]);
      
      setPerDiemClaims(perDiemData);
      setExpenseClaims(expenseData);
    //   setTravelClaims(travelData);
    } catch (err) {
      setError(err.message || 'Failed to fetch claims data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchAllClaims();
  }, [fetchAllClaims]);

  return {
    loading,
    error,
    claimType,
    setClaimType: handleSetClaimType,
    statusFilter,
    setStatusFilter: handleSetStatusFilter,
    activeClaims,
    typeClaims,
    refreshClaims: fetchAllClaims,
    // Selection
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    deleteSelected,
    deleting,
    isDraftView: statusFilter === 'draft'
  };
};

export default useClaimsDashboard;


